import React, { useEffect, useState } from "react";
import { Backdrop, CircularProgress } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import {
    ResponsibleCard,
    ResponsibleGrid,
    ResponsibleName,
    ResponsiblePhoto,
    ResponsiblePhotoPlaceholder,
    ResponsibleRole,
    TierBlock,
    TierLabel,
} from "pages/organizationalStructure/organizationalStructure.styles";
import MunicipalPortalAAppService from "application/platform/municipal-portal-service";

const CARGO_LABEL: Record<number, string> = {
    1: "Presidente",
    2: "Vice-Presidente",
    3: "1º Secretário",
    4: "2º Secretário",
};

interface ComposicaoAtual {
    id: number;
    idVereador: number;
    nomVereador: string;
    fotoUrl?: string;
    indCargo: number;
}

// Mesma hierarquia de duas camadas usada em Estrutura Organizacional (Prefeitura): liderança em
// destaque acima, apoio direto abaixo — aqui, Presidência (Presidente/Vice) e Secretaria da Mesa.
export function MesaDiretoraFactory() {
    const [composicao, setComposicao] = useState<ComposicaoAtual[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        (async () => {
            setLoading(true);
            try {
                const data = await MunicipalPortalAAppService.listComposicaoMesaDiretora();
                setComposicao(data || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const getInitials = (name: string) =>
        (name || "")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join("");

    const presidencia = composicao.filter((c) => c.indCargo === 1 || c.indCargo === 2)
        .sort((a, b) => a.indCargo - b.indCargo);
    const secretaria = composicao.filter((c) => c.indCargo === 3 || c.indCargo === 4)
        .sort((a, b) => a.indCargo - b.indCargo);

    const renderTier = (items: ComposicaoAtual[]) => (
        <ResponsibleGrid>
            {items.map((item) => (
                <ResponsibleCard key={item.id} as="div">
                    {item.fotoUrl ? (
                        <ResponsiblePhoto src={item.fotoUrl} alt={item.nomVereador} />
                    ) : (
                        <ResponsiblePhotoPlaceholder>
                            {getInitials(item.nomVereador)}
                        </ResponsiblePhotoPlaceholder>
                    )}
                    <ResponsibleName>{item.nomVereador}</ResponsibleName>
                    <ResponsibleRole>{CARGO_LABEL[item.indCargo] ?? item.indCargo}</ResponsibleRole>
                </ResponsibleCard>
            ))}
        </ResponsibleGrid>
    );

    return (
        <>
            <Breadcrumb
                items={[
                    { label: "Home", href: "/" },
                    { label: "Mesa Diretora" },
                ]}
            />
            <PageTitle>MESA DIRETORA</PageTitle>
            <Wrapper>
                {!loading && composicao.length === 0 && (
                    <p>Nenhum cargo da mesa diretora atribuído no momento.</p>
                )}

                {presidencia.length > 0 && (
                    <TierBlock>
                        <TierLabel>Presidência</TierLabel>
                        {renderTier(presidencia)}
                    </TierBlock>
                )}

                {secretaria.length > 0 && (
                    <TierBlock>
                        <TierLabel>Secretaria da Mesa</TierLabel>
                        {renderTier(secretaria)}
                    </TierBlock>
                )}
            </Wrapper>
            <Backdrop open={loading} style={{ zIndex: 1000 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}
